import {z} from 'zod'
import {
    AslDistrict,
    City,
    Contact,
    Country,
    MaritalStatus,
    Municipality,
    PatientAgeRange,
    Province,
    TreatmentRequestEligibilityStatus
} from '@/types.ts'
import i18n from '@/translations/i18n'
import {
    TreatmentRequestAuthorization,
    TreatmentRequestDisease,
    TreatmentRequestScore,
    TreatmentRequestSmall,
    TreatmentRequestType
} from '@/features/treatment-requests/types'
import {isValidPhoneNumber} from 'react-phone-number-input'
import {ProjectSender, ProjectSmall} from '@/features/projects/types.ts'
import {NotificationUsersConfirmationValidationSchema} from '../users/types'
import {MedicalReportAttributeKey} from '@/features/medical-reports/types'

export const PatientDetailsUrlParams = z.object({id: z.string().min(1)})
export type PatientDetailsUrlParams = z.infer<typeof PatientDetailsUrlParams>

export const PatientDetailsSearchParams = z.object({
    sidebarSection: z.enum(['activities']).default('activities')
})

export const PatientOccupation = z.enum([
    'student',
    'seeking_first_job',
    'employed',
    'homemaker',
    'unemployed',
    'other_condition',
    'not_declared'
])
export type PatientOccupation = z.infer<typeof PatientOccupation>

export const PatientQualification = z.enum([
    'bachelor_degree',
    'doctorate',
    'elementary_license',
    'high_school_diploma',
    'master_degree',
    'middle_school_diploma',
    'none',
    'not_declared',
    'old_system_degree',
    'university_diploma',
    'university_master',
    'vocational_school_diploma'
])
export type PatientQualification = z.infer<typeof PatientQualification>

export const PatientTransportationMode = z.enum([
    'asl_vehicle',
    'local_authority_vehicle',
    'other',
    'own_vehicle',
    'provider_vehicle'
])
export type PatientTransportationMode = z.infer<typeof PatientTransportationMode>

export const PatientDisability = z.enum(['none', 'requested', 'recognized'])
export type PatientDisability = z.infer<typeof PatientDisability>

export const PatientTaxCodeType = z.enum(['stp_code', 'team_card', 'temporary_code', 'fiscal_code'])
export type PatientTaxCodeType = z.infer<typeof PatientTaxCodeType>

export const PatientStatus = z.enum(['active', 'deceased', 'inactive', 'suspended'])
export type PatientStatus = z.infer<typeof PatientStatus>

const PatientBase = z.object({
    birthDate: z.string().min(1),
    age: z.number().int().nullish(),
    canBeDeleted: z.boolean(),
    contacts: Contact.array(),
    education: PatientQualification,
    fieldsToCompile: z.string().array(),
    fullName: z.string().min(1),
    id: z.string().min(1),
    maritalStatus: MaritalStatus,
    occupation: PatientOccupation,
    residence: z
        .object({
            address: z.string().min(1),
            cap: z.string().min(1),
            municipality: Municipality.nullable(),
            latitude: z.string().optional(),
            longitude: z.string().optional(),
            city: z.object({
                name: z.string().min(1),
                latitude: z.string().min(1),
                longitude: z.string().min(1),
                provinceAcronym: z.string().min(1)
            }),
            id: z.string().min(1),
            streetNumber: z.string().min(1)
        })
        .nullable(),
    residenceAslDistrict: z
        .object({
            id: z.string().min(1),
            name: z.string().min(1),
            aslId: z.string().min(1),
            aslName: z.string().min(1)
        })
        .nullable(),
    domicileAslDistrict: z
        .object({
            id: z.string().min(1),
            name: z.string().min(1),
            aslId: z.string().min(1),
            aslName: z.string().min(1)
        })
        .nullable(),
    neuromuscularDiagnosis: z
        .object({
            id: z.string().min(1),
            name: z.string()
        })
        .nullable(),
    neuromuscularDiagnosisId: z.string().min(1).nullable(),
    serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
    project: ProjectSmall.pick({
        id: true,
        startDate: true,
        expirationDate: true,
        treatmentRequestId: true,
        status: true,
        code: true,
        type: true,
        isSuspended: true
    }).nullable(),
    taxCode: z.string().min(1)
})

export const PatientSmall = PatientBase.merge(z.object({treatmentRequestId: z.string().min(1).nullable()}))
export type PatientSmall = z.infer<typeof PatientSmall>

export const PatientExtended = PatientBase.and(
    z.object({
        firstName: z.string().min(1),
        lastName: z.string().min(1),
        birthCity: z
            .object({
                id: z.string(),
                name: z.string().min(1),
                provinceAcronym: z.string().min(1)
            })
            .nullable(),
        birthCountry: z.object({
            id: z.string().min(1),
            name: z.string().min(1)
        }),
        birthDate: z.string().min(1).nullable(),
        citizenship: z.object({
            id: z.string().min(1),
            name: z.string().min(1)
        }),
        consentDate: z.string().nullable(),
        consentDateFm01: z.string().nullable(),
        createdAt: z.string().min(1),
        disability: PatientDisability,
        domicile: z
            .object({
                address: z.string().min(1),
                latitude: z.string().optional(),
                longitude: z.string().optional(),
                cap: z.string().min(1),
                city: z.object({
                    name: z.string().min(1),
                    latitude: z.string().min(1),
                    longitude: z.string().min(1),
                    provinceAcronym: z.string().min(1)
                }),
                municipality: Municipality.nullable(),
                id: z.string().min(1),
                streetNumber: z.string().min(1)
            })
            .nullable(),
        domicileAslDistrict: z
            .object({
                id: z.string().min(1),
                name: z.string().min(1),
                aslId: z.string().min(1),
                aslName: z.string().min(1)
            })
            .nullable(),
        fm01: z.boolean(),
        gender: z.enum(['male', 'female']),
        legalProtection: z.boolean(),
        localAuthorityAssistanceService: z.boolean(),
        notes: z.string().nullable(),
        privacy1: z.boolean(),
        privacy2: z.boolean(),
        privacy3: z.boolean(),
        privacy4: z.boolean(),
        privacyA: z.boolean().nullable(),
        privacyB: z.boolean().nullable(),
        privacyC: z.boolean().nullable(),
        privacyD: z.boolean().nullable(),
        protectedCategory: z.boolean(),
        serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
        status: PatientStatus,
        taxCodeType: PatientTaxCodeType,
        transportationMode: PatientTransportationMode,
        vademecum: z.boolean().nullable(),
        treatmentRequest: z
            .object({
                id: z.string().min(1),
                code: z.number(),
                authorization: TreatmentRequestSmall.shape['authorization'].nullable(),
                authorizationDate: z.string().nullable(),
                status: TreatmentRequestSmall.shape['status'],
                sector: z.enum(['outpatient', 'domiciliary']),
                type: TreatmentRequestSmall.shape['type']
            })
            .nullable(),
        treatmentRequestEligibility: z.object({
            canRequest: z.boolean(),
            canCreateDirectProject: z.boolean(),
            error: z
                .object({
                    code: TreatmentRequestEligibilityStatus,
                    message: z.string()
                })
                .nullable()
        }),
        medicalRecord: z
            .object({
                id: z.string().min(1),
                code: z.number(),
                coverAttachment: z
                    .object({
                        url: z.string().url(),
                        presignedUrl: z.string().url().nullable().optional()
                    })
                    .nullable(),
                pdfGenerationStartedAt: z.string().nullable(),
                pdfGenerationCompletedAt: z.string().nullable()
            })
            .nullable(),
        project: ProjectSmall.pick({
            id: true,
            status: true,
            isOpeningEventCompleted: true,
            code: true
        }).nullable()
    })
)
export type PatientExtended = z.infer<typeof PatientExtended>

export const NeuroMusocolarDiagnosis = z.object({
    id: z.string(),
    name: z.string()
})
export type NeuroMusocolarDiagnosis = z.infer<typeof NeuroMusocolarDiagnosis>

export const PatientLastInfo = z.object({
    project: z
        .object({
            id: z.string(),
            durationDays: z.number().nullable(),
            code: z.number(),
            type: z.enum(['cyclic', 'continuous']),
            status: z.enum(['opening', 'open', 'closed']),
            startDate: z.string(),
            expirationDate: z.string().nullable(),
            sector: z.enum(['outpatient', 'domiciliary', 'mixed', 'extended', 'maintenance']),
            numberOfCycles: z.enum(['one_cycle', 'two_cycles', 'single_non_repeatable_cycle']).nullable(),
            assistiveRegime: z.enum(['autism', 'adults']).nullable(),
            sender: ProjectSender.nullable(),
            specialistId: z.string().nullable()
        })
        .nullable(),
    treatmentRequest: z
        .object({
            id: z.string(),
            type: TreatmentRequestType,
            authorization: TreatmentRequestAuthorization,
            sector: z.enum(['outpatient', 'domiciliary', 'mixed', 'extended', 'maintenance']),
            disease: TreatmentRequestDisease,
            scores: z.array(TreatmentRequestScore),
            referringDoctor: z.object({id: z.string().min(1), fullName: z.string().min(1)}).nullable(),
            therapists: z.array(z.object({id: z.string().min(1), fullName: z.string().min(1)})).nullable()
        })
        .nullable(),
    cyclicalReportClosingInfo: z.array(
        z.object({
            id: z.string().min(1),
            name: MedicalReportAttributeKey,
            value: z.string(),
            object: z.record(z.string(), z.unknown()).array().nullish()
        })
    )
})
export type PatientLastInfo = z.infer<typeof PatientLastInfo>

/**
 * Schema for validating the personal data of a patient.
 * Includes fields like name, gender, birth date, tax code, and more.
 * Contains specific validations based on the patient's birth location and tax code type.
 */
export const PatientPersonalDataSchema = z.object({
    lastName: z
        .string()
        .min(1, i18n.t('errors:required'))
        .trim()
        .regex(/^[a-zA-Zàèìòùáéíóúäëïöüâêîôûñçß\s'-]+$/i, {message: i18n.t('errors:characters')}),
    firstName: z
        .string()
        .min(1, i18n.t('errors:required'))
        .trim()
        .regex(/^[a-zA-Zàèìòùáéíóúäëïöüâêîôûñçß\s'-]+$/i, {message: i18n.t('errors:characters')}),
    gender: z.string({invalid_type_error: i18n.t('errors:required')}).min(4),
    birthDate: z.date({
        errorMap: issue => ({
            message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
        })
    }),
    taxCodeType: PatientTaxCodeType.nullish(),
    taxCode: z.string().min(1, i18n.t('errors:required')).toUpperCase(),
    maritalStatus: z.string({required_error: i18n.t('errors:required')}).min(1),
    education: z.string({required_error: i18n.t('errors:required')}).min(1),
    occupation: z.string({required_error: i18n.t('errors:required')}).min(1),
    serviceType: PatientAgeRange.exclude(['evolutionary_age_adults'])
})

export const PatientPersonalDataFormValidationSchema = PatientPersonalDataSchema.refine(
    data => {
        if (data.taxCodeType == 'fiscal_code') {
            const fiscalCodeRegex = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/
            return fiscalCodeRegex.test(data.taxCode)
        }

        return true
    },
    {
        message: i18n.t('errors:invalid_tax_code'),
        path: ['taxCode']
    }
).and(
    z
        .object({
            isBornAbroad: z.boolean().default(false),
            birthProvince: Province.nullish(),
            birthCountry: Country.nullish(),
            birthCity: z.object(City.shape).nullish(),
            citizenship: Country.nullable()
        })
        .refine(values => values.isBornAbroad || (!values.isBornAbroad && values.birthCity), {
            message: i18n.t('errors:required'),
            path: ['birthCity']
        })
        .refine(values => !values.isBornAbroad || (values.isBornAbroad && values.birthCountry), {
            message: i18n.t('errors:required'),
            path: ['birthCountry']
        })
        .refine(values => values.isBornAbroad || (!values.isBornAbroad && values.birthProvince), {
            message: i18n.t('errors:required'),
            path: ['birthProvince']
        })
        .refine(values => values.citizenship, {
            message: i18n.t('errors:required'),
            path: ['citizenship']
        })
)
export type PatientPersonalDataFormValidationSchema = z.infer<typeof PatientPersonalDataFormValidationSchema>

/**
 * Schema for validating the residence information of a patient.
 * It includes fields like citizenship, address, city, and province.
 * Additional validations ensure consistency of residence data when the citizenship is Italy.
 */
export const PatientResidenceSchema = z.object({
    citizenship: Country.nullish(),
    residence: z
        .object(
            {
                address: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required')),
                city: z
                    .object(City.omit({id: true, longitude: true, latitude: true}).shape, {
                        required_error: i18n.t('errors:required')
                    })
                    .nullable(),
                municipality: z
                    .object({
                        id: z.string().min(1),
                        name: z.string().min(1)
                    })
                    .nullish(),
                latitude: z.string().optional(),
                longitude: z.string().optional(),
                province: z.object(Province.shape, {required_error: i18n.t('errors:required')}).nullable(),
                postalCode: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required')),
                streetNumber: z.string().optional()
            },
            {required_error: i18n.t('errors:required'), invalid_type_error: i18n.t('errors:required')}
        )
        .nullish(),
    residenceAslDistrict: z.object(AslDistrict.shape, {required_error: i18n.t('errors:required')}).nullish()
})

export const PatientResidenceFormValidationSchema = PatientResidenceSchema.refine(
    values =>
        values.citizenship &&
        ((values.citizenship.name == 'Italia' && values.residence) || values.citizenship.name != 'Italia'),
    {
        message: i18n.t('errors:required'),
        path: ['residence']
    }
)
export type PatientResidenceFormValidationSchema = z.infer<typeof PatientResidenceFormValidationSchema>

export const PatientResidenceManualFormModalValidationSchema = z
    .object(PatientResidenceSchema.shape)
    .superRefine((values, context) => {
        if (!values?.residence?.city) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['residence.city']
            })
        }
        if (!values?.residence?.province) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['residence.province']
            })
        }
    })
export type PatientResidenceManualFormModalValidationSchema = z.infer<
    typeof PatientResidenceManualFormModalValidationSchema
>

/**
 * Schema for validating domicile information of a patient.
 * Handles details about the patient's domicile including address, city, and municipality.
 * Ensures data consistency for patients who have a different domicile address.
 */
export const PatientDomicileSchema = z.object({
    domicile: z
        .object({
            address: z.string().optional(),
            streetNumber: z.string().optional(),
            latitude: z.string().optional(),
            longitude: z.string().optional(),
            city: z.object(City.omit({id: true, longitude: true, latitude: true}).shape).nullish(),
            province: z.object(Province.shape, {required_error: i18n.t('errors:required')}).nullish(),
            postalCode: z.string().optional(),
            municipality: z
                .object({
                    id: z.string().min(1),
                    name: z.string().min(1)
                })
                .nullish()
        })
        .nullish(),
    domicileAslDistrict: z.object(AslDistrict.shape, {required_error: i18n.t('errors:required')}).nullish()
})

export const PatientDomicileFormSchema = PatientDomicileSchema.and(
    z.object({
        citizenship: Country.nullish(),
        isDifferentDomicile: z.boolean().default(false)
    })
).refine(
    values =>
        values.citizenship?.name != 'Italia' ||
        !values.isDifferentDomicile ||
        (values.isDifferentDomicile && values.citizenship.name == 'Italia' && values.domicile?.address),
    {
        message: i18n.t('errors:required'),
        path: ['domicile']
    }
)
export type PatientDomicileFormSchema = z.infer<typeof PatientDomicileFormSchema>

/**
 * Validation schema for domicile form of a patient.
 * Refines the PatientDomicileFormSchema by adding specific field validations when 'isDifferentDomicile' is true.
 */
export const PatientDomicileFormValidationSchema = PatientDomicileFormSchema.refine(
    values => !values.isDifferentDomicile || (values.isDifferentDomicile && values?.domicile?.address),
    {
        message: i18n.t('errors:required'),
        path: ['domicile.address']
    }
)
    .refine(values => !values.isDifferentDomicile || (values.isDifferentDomicile && values?.domicile?.city), {
        message: i18n.t('errors:required'),
        path: ['domicile.city']
    })
    .refine(values => !values.isDifferentDomicile || (values.isDifferentDomicile && values?.domicile?.province), {
        message: i18n.t('errors:required'),
        path: ['domicile.province']
    })
    .refine(values => !values.isDifferentDomicile || (values.isDifferentDomicile && values?.domicile?.postalCode), {
        message: i18n.t('errors:required'),
        path: ['domicile.postalCode']
    })
export type PatientDomicileFormValidationSchema = z.infer<typeof PatientDomicileFormValidationSchema>

export const PatientDomicileManualFormValidationSchema = PatientDomicileSchema.superRefine((values, context) => {
    if (!values.domicile?.city) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('errors:required'),
            path: ['domicile.city']
        })
    }
    if (!values.domicile?.province) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('errors:required'),
            path: ['domicile.province']
        })
    }
})
export type PatientDomicileManualFormValidationSchema = z.infer<typeof PatientDomicileManualFormValidationSchema>

/**
 * Schema for validating the contacts information of a patient.
 * Ensures each contact contains essential information like the label.
 */
export const PatientContactsSchema = z.array(
    z
        .object({
            id: z.string().min(1).optional(),
            label: z.string({required_error: i18n.t('errors:missing_data')}).min(1, i18n.t('errors:missing_data'))
        })
        .and(
            z
                .object({
                    type: z.enum(['mobile_phone', 'telephone']),
                    contact: z
                        .string({required_error: i18n.t('errors:missing_data')})
                        .min(1, {message: i18n.t('errors:missing_data')})
                        .refine(value => isValidPhoneNumber(value), {
                            message: i18n.t('errors:phone_number')
                        })
                })
                .or(
                    z.object({
                        type: z.literal('email'),
                        contact: z
                            .string({required_error: i18n.t('errors:missing_data')})
                            .email({message: i18n.t('errors:email_error')})
                            .transform(val => val.toLowerCase().trim())
                    })
                )
        )
)
export type PatientContactsSchema = z.infer<typeof PatientContactsSchema>

export const PatientContactsFormValidationSchema = z.object({
    contacts: PatientContactsSchema.min(2, {message: i18n.t('errors:missing_data')})
})
export type PatientContactsFormValidationSchema = z.infer<typeof PatientContactsFormValidationSchema>

/**
 * Schema for validating the medical information of a patient.
 * Contains fields for neuromuscular diagnosis, legal protection, transportation mode, and more.
 */
export const PatientMedicalInfoFormValidationSchema = z.object({
    neuromuscularDiagnosis: z.object(NeuroMusocolarDiagnosis.shape).nullish(),
    protectedCategory: z.enum(['belonging', 'notBelonging'], {required_error: i18n.t('errors:required')}),
    legalProtection: z.enum(['subjectTo', 'notSubjectTo'], {required_error: i18n.t('errors:required')}),
    transportationMode: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required')),
    localAuthorityAssistanceService: z.enum(['provided', 'notProvided'], {required_error: i18n.t('errors:required')}),
    disability: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required'))
})
export type PatientMedicalInfoFormValidationSchema = z.infer<typeof PatientMedicalInfoFormValidationSchema>

/**
 * Schema for validating patient privacy settings.
 * Handles privacy-related consents, including the specific consent dates where required.
 */
export const PatientPrivacyFormValidationSchema = z
    .object({
        fm01: z.boolean().default(false),
        privacy1: z.boolean().default(false),
        privacy2: z.boolean().default(false),
        privacy3: z.boolean().default(false),
        privacy4: z.boolean().default(false),
        consentDateFm01: z
            .date({
                errorMap: issue => ({
                    message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
                })
            })
            .nullish()
    })
    .refine(values => !values.fm01 || (values.fm01 && values.consentDateFm01), {
        message: i18n.t('errors:required'),
        path: ['consentDateFm01']
    })
export type PatientPrivacyFormValidationSchema = z.infer<typeof PatientPrivacyFormValidationSchema>

/**
 * Schema for validating notes related to the patient.
 * Notes are optional and can be used to store any additional information.
 */
export const PatientNotesFormValidationSchema = z.object({
    notes: z.string().nullish()
})
export type PatientNotesFormValidationSchema = z.infer<typeof PatientNotesFormValidationSchema>

/**
 * Schema for validating the vademecum information of a patient.
 * Handles the vademecum consent.
 */
export const PatientVademecumFormValidationSchema = z.object({
    vademecum: z.boolean().nullish()
})
export type PatientVademecumFormValidationSchema = z.infer<typeof PatientVademecumFormValidationSchema>

/**
 * Main schema that combines all the subform schemas.
 * It integrates the patient personal data, residence, domicile, contacts, medical information,
 * privacy consents, notes used for create and update operations
 */
export const PatientFormValidationSchema = PatientPersonalDataFormValidationSchema.and(
    PatientResidenceFormValidationSchema
)
    .and(PatientDomicileFormValidationSchema)
    .and(PatientContactsFormValidationSchema)
    .and(PatientMedicalInfoFormValidationSchema)
    .and(PatientPrivacyFormValidationSchema)
    .and(PatientNotesFormValidationSchema)
    .and(PatientVademecumFormValidationSchema)
export type PatientFormValidationSchema = z.infer<typeof PatientFormValidationSchema>

/**
 * Main schema that combines all the subform schemas with notification settings
 */
export const CreatePatientFormValidationSchema = PatientFormValidationSchema.and(
    NotificationUsersConfirmationValidationSchema
)
export type CreatePatientFormValidationSchema = z.infer<typeof CreatePatientFormValidationSchema>

export const BasePatientsColumns = z.enum([
    'fullName',
    'birthDate',
    'age',
    'serviceType',
    'outRegion',
    'residenceAslDistrictId',
    'residenceCityId',
    'residenceProvinceId',
    'neuromuscularDiagnosisId',
    'mobilePhone',
    'telephone',
    'email',
    'projectStatus'
])

export const PatientsColumn = z.enum([...BasePatientsColumns.options, 'moreActions', 'select'])
export type PatientsColumn = z.infer<typeof PatientsColumn>

export const PatientEventsColumn = z.enum([
    'select',
    'date',
    'time',
    'status',
    'accessType',
    'consultantId',
    'sector',
    'isRemote',
    'projectCode',
    'projectStartDate',
    'projectExpirationDate'
])
export type PatientEventsColumn = z.infer<typeof PatientEventsColumn>

export const PatientMedicalRecordColumn = z.enum([
    'reportDate',
    'reportType',
    'status',
    'consultant',
    'signedFor',
    'projectCode',
    'projectStartDate',
    'projectExpirationDate',
    'projectStatus',
    'moreActions'
])
export type PatientMedicalRecordColumn = z.infer<typeof PatientMedicalRecordColumn>
